import React, { useEffect, useState } from 'react';
import api from '../../services/api';

interface FeedbackItem {
  id: string;
  question_id: string;
  student_id: string;
  student_name: string | null;
  student_email: string | null;
  feedback_type: string;
  description: string | null;
  status: string;
  created_at: string;
  resolved_at: string | null;
  admin_notes: string | null;
}

interface FeedbackStats {
  total: number;
  by_status: Record<string, number>;
  by_type: { type: string; count: number }[];
}

const STATUS_COLORS: Record<string, string> = {
  open: '#ef4444',
  in_progress: '#f59e0b',
  resolved: '#22c55e',
  wont_fix: '#6b7280',
};

const TYPE_LABELS: Record<string, string> = {
  unclear: 'Explanation Unclear',
  answer_wrong: 'Answer Dispute',
  technical: 'Technical Issue',
  other: 'Other',
};

export const FeedbackPanel: React.FC = () => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [listRes, statsRes] = await Promise.all([
        api.get(`/api/feedback/admin/list${params}`),
        api.get('/api/feedback/admin/stats'),
      ]);
      setItems(listRes.data.items);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const handleEdit = (item: FeedbackItem) => {
    setEditingId(item.id);
    setEditNotes(item.admin_notes || '');
    setEditStatus(item.status);
  };

  const handleSave = async (id: string) => {
    try {
      await api.patch(`/api/feedback/admin/${id}`, {
        status: editStatus,
        admin_notes: editNotes,
      });
      setEditingId(null);
      fetchData();
    } catch (err) {
      console.error('Failed to update feedback:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && items.length === 0) {
    return <div className="admin-loading">Loading feedback...</div>;
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  return (
    <div className="feedback-panel">
      {/* Stats Summary */}
      {stats && (
        <div className="feedback-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Feedback</span>
          </div>
          <div className="stat-card">
            <span className="stat-value" style={{ color: STATUS_COLORS.open }}>
              {stats.by_status.open || 0}
            </span>
            <span className="stat-label">Open</span>
          </div>
          <div className="stat-card">
            <span className="stat-value" style={{ color: STATUS_COLORS.in_progress }}>
              {stats.by_status.in_progress || 0}
            </span>
            <span className="stat-label">In Progress</span>
          </div>
          <div className="stat-card">
            <span className="stat-value" style={{ color: STATUS_COLORS.resolved }}>
              {stats.by_status.resolved || 0}
            </span>
            <span className="stat-label">Resolved</span>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="feedback-filter">
        <label>Filter by status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="wont_fix">Won't Fix</option>
        </select>
      </div>

      {/* Feedback List */}
      <div className="feedback-list">
        {items.length === 0 ? (
          <div className="no-feedback">No feedback found</div>
        ) : (
          items.map((item) => (
            <div key={item.id} className="feedback-item">
              <div className="feedback-header">
                <span
                  className="feedback-status"
                  style={{ background: STATUS_COLORS[item.status] }}
                >
                  {item.status.replace('_', ' ')}
                </span>
                <span className="feedback-type">
                  {TYPE_LABELS[item.feedback_type] || item.feedback_type}
                </span>
                <span className="feedback-date">{formatDate(item.created_at)}</span>
              </div>

              <div className="feedback-body">
                <div className="feedback-question">
                  <strong>Question ID:</strong> {item.question_id}
                </div>
                <div className="feedback-student">
                  <strong>Student:</strong> {item.student_name || item.student_email || item.student_id}
                </div>
                {item.description && (
                  <div className="feedback-description">
                    <strong>Details:</strong> {item.description}
                  </div>
                )}
              </div>

              {editingId === item.id ? (
                <div className="feedback-edit">
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="wont_fix">Won't Fix</option>
                  </select>
                  <textarea
                    placeholder="Admin notes..."
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={2}
                  />
                  <div className="edit-actions">
                    <button className="save-btn" onClick={() => handleSave(item.id)}>
                      Save
                    </button>
                    <button className="cancel-btn" onClick={() => setEditingId(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="feedback-footer">
                  {item.admin_notes && (
                    <div className="admin-notes">
                      <strong>Notes:</strong> {item.admin_notes}
                    </div>
                  )}
                  <button className="edit-btn" onClick={() => handleEdit(item)}>
                    Edit
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
